import { useRouter } from 'next/router';
import ContainerXL from 'Nori/Container/ContainerXL';
import Link from 'next/link';
import Image from 'Nori/Image';
import styles from './DesktopMenu.module.scss';
import NavigationLinks from './NavigationLinks';
import Button from 'Nori/Button';
import { useAppContext } from 'Providers/App';
import { finderHandlerClick } from 'Custom/Finder/utils';
import { UI_ELEMENTS } from 'Providers/App/elements';
import {
  useGigyaReady,
  createHandleOpenLogin,
  PATIENT_HUB_CONFIG,
} from 'Utils/hooks/useGigyaService';
import BRAND_LOGO from 'Images/logos/nexplanon-d.webp';

const { modalHealthcare: modalHealthcareId, modalISI: modalISIId } =
  UI_ELEMENTS;

const DesktopMenu = ({
  basic = false,
  loginButton = false,
  logoutButton = false,
}) => {
  const { getElementClickHandler, getElementState } = useAppContext();
  const isGigyaReady = useGigyaReady();
  const router = useRouter();
  const handleOpenLogin = createHandleOpenLogin();

  const modalISIHandler = getElementClickHandler(modalISIId);
  const modalHealthcareHandler = getElementClickHandler(modalHealthcareId);
  const [modalISI, modalHealthcare] = getElementState(
    modalISIId,
    modalHealthcareId
  );

  const handleLogout = () => {
    if (isGigyaReady) {
      window.gigya.accounts.logout();
      router.push(PATIENT_HUB_CONFIG.REGISTRATION_URL);
    }
  };

  return (
    <div className={styles.wrapper} data-qa-tag="desktopMenu-wrapper">
      <div className={styles.flexContainer} data-qa-tag="desktopMenu-container">
        <div
          className={styles.logoContainer}
          data-qa-tag="desktopMenu-logo-container"
        ></div>
        <div
          className={styles.navContainer}
          data-qa-tag="desktopMenu-nav-container"
        >
          <div
            className={basic ? styles.topContainerWhite : styles.topContainer}
            data-qa-tag="desktopMenu-top-container"
          />
        </div>
      </div>
      <div
        className={styles.wrapperContainer}
        data-qa-tag="desktopMenu-wrapper-container"
      >
        <ContainerXL data-qa-tag="desktopMenu-inner-container">
          <nav className={styles.mainNav} data-qa-tag="main-nav">
            <div className={styles.logoWrapper} data-qa-tag="logo-wrapper">
              <Link
                href="/"
                className={styles.nexplanonLogo}
                data-qa-tag="nexplanon-logo-link"
              >
                <Image
                  src={BRAND_LOGO}
                  width={271}
                  height={83}
                  alt="NEXPLANON® (etonogestrel implant) 68 mg Radiopaque Logo"
                  data-design-type="clickAction"
                  data-design-category="top_nav_vd"
                  data-design-action="Logo"
                  data-design-label="Nexplanon logo"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  data-qa-tag="nexplanon-logo-image"
                />
              </Link>
            </div>
            <div
              className={styles.mainNavContainer}
              data-qa-tag="main-nav-conatiner"
            >
              {basic ? (
                <div
                  className={styles.singleNavigation}
                  data-qa-tag="single-navigation"
                >
                  <Link
                    href="https://www.organon.com/product/usa/pi_circulars/n/nexplanon/nexplanon_pi.pdf"
                    target="_blank"
                    rel="noopener"
                    data-design-type="clickAction"
                    data-design-category="top_nav_vd"
                    data-design-action="Download"
                    data-design-label="Prescribing Information"
                    data-design-nori_id="Navigation"
                    data-design-module_name="Navigation"
                    data-design-module_no="00"
                    data-qa-tag="prescribing-information-link"
                  >
                    Prescribing Information
                  </Link>
                  <Link
                    href="https://www.organon.com/product/usa/pi_circulars/n/nexplanon/nexplanon_ppi.pdf"
                    target="_blank"
                    rel="noopener"
                    data-design-type="clickAction"
                    data-design-category="top_nav_vd"
                    data-design-action="Download"
                    data-design-label="Patient Information"
                    data-design-nori_id="Navigation"
                    data-design-module_name="Navigation"
                    data-design-module_no="00"
                    data-qa-tag="patient-information-link"
                  >
                    Patient Information
                  </Link>

                  {loginButton && (
                    <Button
                      size="medium"
                      type="btnWhiteBlue"
                      className={styles.loginButton}
                      data-design-action="Button"
                      data-design-label="Log In"
                      data-design-nori_id="Navigation"
                      data-design-module_name="Navigation"
                      data-design-module_no="00"
                      data-qa-tag="login-button"
                      onClick={handleOpenLogin}
                      disabled={!isGigyaReady}
                    >
                      Log In
                    </Button>
                  )}
                  {logoutButton && (
                    <Button
                      size="medium"
                      type="btnWhiteBlue"
                      className={styles.loginButton}
                      data-design-action="Button"
                      data-design-label="Log Out"
                      data-design-nori_id="Navigation"
                      data-design-module_name="Navigation"
                      data-design-module_no="00"
                      data-qa-tag="logout-button"
                      disabled={!isGigyaReady}
                      onClick={handleLogout}
                    >
                      Log Out
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className={styles.topNavigation}>
                    <button
                      className={styles.link}
                      onClick={modalISIHandler}
                      data-design-category="top_nav_vd"
                      data-design-type="clickAction"
                      data-design-action="Expand"
                      data-design-label="Important Safety Info"
                      data-design-nori_id="Navigation"
                      data-design-module_name="Navigation"
                      data-design-module_no="00"
                      data-qa-tag="important-safety-info-button"
                      aria-expanded={modalISI}
                    >
                      Important Safety Info
                    </button>

                    <Link
                      href="https://www.organon.com/product/usa/pi_circulars/n/nexplanon/nexplanon_pi.pdf"
                      target="_blank"
                      rel="noopener"
                      data-design-type="clickAction"
                      data-design-category="top_nav_vd"
                      data-design-action="Download"
                      data-design-label="Prescribing Info"
                      data-design-nori_id="Navigation"
                      data-design-module_name="Navigation"
                      data-design-module_no="00"
                      data-qa-tag="prescribing-info-link"
                    >
                      Prescribing Info
                    </Link>
                    <Link
                      href="https://www.organon.com/product/usa/pi_circulars/n/nexplanon/nexplanon_ppi.pdf"
                      target="_blank"
                      rel="noopener"
                      data-design-type="clickAction"
                      data-design-category="top_nav_vd"
                      data-design-action="Download"
                      data-design-label="Patient Info"
                      data-design-nori_id="Navigation"
                      data-design-module_name="Navigation"
                      data-design-module_no="00"
                      data-qa-tag="patient-info-link"
                    >
                      Patient Info
                    </Link>
                    <button
                      className={styles.link}
                      onClick={modalHealthcareHandler}
                      data-design-type="clickAction"
                      data-design-category="top_nav_vd"
                      data-design-action="Expand"
                      data-design-label="HCPs"
                      data-design-nori_id="Navigation"
                      data-design-module_name="Navigation"
                      data-design-module_no="00"
                      data-qa-tag="hcps-button"
                      aria-expanded={modalHealthcare}
                    >
                      HCPs
                    </button>
                    <Link
                      href="/es-us/"
                      target="_blank"
                      rel="noopener"
                      data-design-type="clickAction"
                      data-design-category="top_nav_vd"
                      data-design-action="Link"
                      data-design-label="Español"
                      data-design-nori_id="Navigation"
                      data-design-module_name="Navigation"
                      data-design-module_no="00"
                      data-qa-tag="espanol-link"
                    >
                      Español
                    </Link>
                  </div>
                  <div className={styles.mainMenu} data-qa-tag="main-menu">
                    <NavigationLinks />
                    <div
                      className={styles.ctaContainer}
                      data-qa-tag="cta-button-container"
                    >
                      <Button
                        data-design-type="clickAction"
                        data-design-category="top_nav_vd"
                        data-design-action="Button"
                        data-design-label="Find a Doctor"
                        data-design-nori_id="Navigation"
                        data-design-module_name="Navigation"
                        data-design-module_no="00"
                        data-qa-tag="find-a-doctor-button"
                        className={styles.ctaButton}
                        type="btnBlueWhite"
                        size="medium"
                        href="/#find-a-doctor"
                        onClick={finderHandlerClick}
                      >
                        Find a Doctor
                      </Button>
                      <Button
                        data-design-type="clickAction"
                        data-design-category="top_nav_vd"
                        data-design-action="Button"
                        data-design-label="My NEXPLANON"
                        data-design-nori_id="Navigation"
                        data-design-module_name="Navigation"
                        data-design-module_no="00"
                        data-qa-tag="my-nexplanon-button"
                        className={styles.ctaButton}
                        href="/enrollment/"
                        type="btnWhiteBlue"
                        size="medium"
                      >
                        My NEXPLANON
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </nav>
        </ContainerXL>
      </div>
    </div>
  );
};

export default DesktopMenu;
